import { dasherizeKeys } from '@zoocasa/node-kit/objects';
import type { IdentityProvider } from '../identity-provider';
import type { HttpClient } from '../client-app/index';
import type { User } from '../../types';
import { toE164 } from './phone-utils';

interface UpdateProfileResult {
  success: true;
}

interface UpdateEmailResult {
  success: true;
}

interface ConfirmEmailUpdateResult {
  success: true;
}

type AccountManagementError = { error: string };

/**
 * Updates a user's profile attributes (name, phone) using the Cognito-first
 * sequencing pattern: Cognito is updated first; Rails is only called on success.
 *
 * Fields sent to Cognito: given_name, family_name, phone_number (E.164).
 * Fields sent to Rails: full User object (same as the existing updateUserData path).
 */
export async function cognitoUpdateProfile(
  idp: IdentityProvider,
  http: HttpClient,
  params: User,
): Promise<UpdateProfileResult | AccountManagementError> {
  const phone = params.homePhone || undefined;
  await idp.updateProfileAttributes({
    givenName: params.firstName,
    familyName: params.lastName,
    phoneNumber: phone ? toE164(phone) : undefined,
  });

  await http.request(
    `/services/api/v3/users/${params.id}`,
    'PATCH',
    { data: { attributes: dasherizeKeys(params as unknown as Record<string, unknown>) } },
  );

  return { success: true };
}

/**
 * Initiates an email change.
 *
 * Cognito-first: Cognito sends a verification code to the new email address.
 * Rails is updated immediately after (eager update; Cognito change is pending confirmation).
 *
 * The caller must follow up with cognitoConfirmEmailUpdate() once the user
 * supplies the verification code.
 */
export async function cognitoUpdateEmail(
  idp: IdentityProvider,
  http: HttpClient,
  currentUser: User,
  newEmail: string,
  retypeEmail: string,
  password?: string,
  passwordConfirmation?: string,
): Promise<UpdateEmailResult | AccountManagementError> {
  await idp.updateEmailAttribute(newEmail);

  const data = {
    data: {
      type: 'users',
      attributes: {
        currentEmail: currentUser.email,
        email: newEmail,
        emailConfirmation: retypeEmail,
        password,
        passwordConfirmation,
      },
    },
  };

  await http.request('/services/api/v3/users/email', 'PATCH', data);

  return { success: true };
}

/**
 * Commits a pending Cognito email change by confirming the verification code
 * sent to the new email address.
 *
 * Rails was already updated in the cognitoUpdateEmail() step — no second API
 * call is needed here.
 */
export async function cognitoConfirmEmailUpdate(
  idp: IdentityProvider,
  code: string,
): Promise<ConfirmEmailUpdateResult | AccountManagementError> {
  await idp.confirmEmailAttribute(code);
  return { success: true };
}
